import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge } from '@/backoffice/components/ui';
import { useBackofficeRole } from '@/backoffice/hooks/useBackofficeRole';
import { getDashboardBase } from '@/backoffice/config/navigation';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  type Notification,
} from '@/domains/client/services/client.service';
import {
  fetchUserNotifications,
  markUserNotificationRead,
  markAllUserNotificationsRead,
} from '@/domains/api/notifications.api';
import { formatRelativeTime } from '@/backoffice/utils';
import { Bell, CheckCheck, Loader2, Wifi } from 'lucide-react';

export function SharedNotificationsPage() {
  const userType = useBackofficeRole();
  const navigate = useNavigate();
  const dashboardBase = getDashboardBase(userType);
  const [notifications, setNotifications] = useState<Array<{ id: string | number; titre: string; message: string; est_lu: boolean; created_at: string; reference_id?: number | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (userType === 'client') {
      fetchNotifications()
        .then((list) => setNotifications(list.map((n) => ({
          id: n.id,
          titre: n.titre,
          message: n.message,
          est_lu: n.est_lu,
          created_at: n.created_at,
          reference_id: n.reference_id,
        }))))
        .catch(() => setNotifications([]))
        .finally(() => setLoading(false));
      return;
    }

    fetchUserNotifications()
      .then((data) => {
        setNotifications(data.notifications.map((n) => ({
          id: n.id,
          titre: n.title,
          message: n.body,
          est_lu: n.is_read,
          created_at: n.created_at ?? '',
          reference_id: typeof n.data?.order_request_id === 'number' ? n.data.order_request_id : null,
        })));
        setLive(true);
      })
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  }, [userType]);

  const handleRead = async (n: typeof notifications[0]) => {
    if (!n.est_lu) {
      if (userType === 'client') {
        await markNotificationAsRead(String(n.id));
      } else {
        await markUserNotificationRead(Number(n.id));
      }
      setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, est_lu: true } : item)));
    }
    if (n.reference_id) {
      if (userType === 'client') {
        navigate(`${dashboardBase}/commandes/${n.reference_id}`);
      } else if (userType === 'pro_driver' || userType === 'community_driver') {
        navigate(`${dashboardBase}/missions`);
      }
    }
  };

  const handleReadAll = async () => {
    if (userType === 'client') {
      await markAllNotificationsAsRead();
    } else {
      await markAllUserNotificationsRead();
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, est_lu: true })));
  };

  return (
    <DashboardLayout userType={userType} title="Notifications">
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            {live && userType !== 'client' && (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <Wifi className="w-3.5 h-3.5" /> Notifications en temps réel
              </p>
            )}
            <Button variant="outline" size="sm" leftIcon={<CheckCheck className="w-4 h-4" />} onClick={handleReadAll} className="ml-auto">
              Tout marquer comme lu
            </Button>
          </div>
          <div className="space-y-3">
            {notifications.length === 0 ? (
              <Card><p className="text-center text-gray-500 py-8">Aucune notification</p></Card>
            ) : notifications.map((n) => (
              <Card key={String(n.id)} className={!n.est_lu ? 'border-indigo-200 bg-indigo-50/30' : ''}>
                <button type="button" className="w-full text-left" onClick={() => handleRead(n)}>
                  <div className="flex justify-between gap-4">
                    <div>
                      <p className="font-medium">{n.titre}</p>
                      <p className="text-sm text-gray-600 mt-1">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{formatRelativeTime(n.created_at)}</p>
                    </div>
                    {!n.est_lu && <Badge variant="primary">Nouveau</Badge>}
                  </div>
                </button>
              </Card>
            ))}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
